import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

const Agent = () => {
  const navigate = useNavigate();
  const [goal, setGoal] = useState('');
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [allowed, setAllowed] = useState(true);
  const [planByRun, setPlanByRun] = useState({});
  const [applyingRun, setApplyingRun] = useState('');
  const [planType, setPlanType] = useState('FREE');
  const [agentUsage, setAgentUsage] = useState({ used: 0, limit: null });

  const loadRuns = async () => {
    try {
      const [runsRes, subRes, usageRes] = await Promise.all([
        axios.get('/api/agent/runs'),
        axios.get('/api/subscription/status'),
        axios.get('/api/subscription/usage'),
      ]);
      setRuns(runsRes.data || []);
      setPlanType(subRes?.data?.plan_type || 'FREE');
      setAgentUsage({
        used: usageRes?.data?.agent_runs_used || 0,
        limit: usageRes?.data?.agent_runs_limit ?? null,
      });
      setAllowed(true);
    } catch (e) {
      if (e?.response?.status === 403) {
        try {
          const subRes = await axios.get('/api/subscription/status');
          const usageRes = await axios.get('/api/subscription/usage');
          setPlanType(subRes?.data?.plan_type || 'FREE');
          setAgentUsage({
            used: usageRes?.data?.agent_runs_used || 0,
            limit: usageRes?.data?.agent_runs_limit ?? null,
          });
        } catch {}
        setAllowed(false);
        return;
      }
      toast.error('Failed to load agent runs');
    }
  };

  useEffect(() => { loadRuns(); }, []);

  const runAgent = async () => {
    const g = goal.trim();
    if (!g) return toast.error('Enter a goal first');
    try {
      setLoading(true);
      await axios.post('/api/agent/runs', { goal: g, max_steps: 6 });
      setGoal('');
      toast.success('Agent run completed');
      loadRuns();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to run agent');
    } finally {
      setLoading(false);
    }
  };

  const generateMindmap = async (runId) => {
    try {
      const res = await axios.post('/api/agent/mindmap', { run_id: runId });
      const mm = res.data;
      toast.success('Mindmap generated');
      if (mm?.id) navigate(`/mindmaps?open=${mm.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to generate mindmap');
    }
  };

  const generateDoc = async (runId, docType) => {
    try {
      const res = await axios.post('/api/agent/documents/generate', {
        run_id: runId,
        doc_type: docType,
      });
      const doc = res.data;
      const blob = new Blob([doc?.markdown || ''], { type: 'text/markdown;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(doc?.title || docType).replace(/\s+/g, '_').toLowerCase()}.md`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Document generated and downloaded');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to generate document');
    }
  };

  const generatePlan = async (runId) => {
    try {
      const res = await axios.post('/api/agent/plan', { run_id: runId });
      setPlanByRun((prev) => ({ ...prev, [runId]: res.data }));
      toast.success('Execution plan generated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to generate execution plan');
    }
  };

  const applyPlan = async (runId) => {
    const plan = planByRun[runId];
    if (!plan?.tasks?.length) return toast.error('Generate a plan first');

    try {
      setApplyingRun(runId);
      const res = await axios.post('/api/agent/plan/apply', {
        tasks: plan.tasks,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
        deliver_telegram: true,
      });
      toast.success(`Applied plan: ${res.data.created_tasks} scheduled tasks created`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to apply execution plan');
    } finally {
      setApplyingRun('');
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-3xl font-bold text-gray-900">Agent Mode</h1>
      <p className="text-gray-600 mt-2">Give Lumina a goal and let it execute multi-step actions.</p>
      {allowed && (
        <p className="text-sm text-gray-500 mt-1">
          {agentUsage.limit === null ? `Plan: ${planType} • Agent runs: unlimited/fair use` : `Plan: ${planType} • Agent runs this month: ${agentUsage.used}/${agentUsage.limit}`}
        </p>
      )}

      {!allowed && (
        <div className="card mt-6 border-yellow-300 bg-yellow-50">
          <p className="text-yellow-800 font-medium">Agent mode is available on PRO (30 runs/month) and ENTERPRISE.</p>
          <a href="/subscription" className="text-sm text-purple-700 hover:text-purple-800">Upgrade your plan</a>
        </div>
      )}

      {allowed && (
        <>
          <div className="card mt-6 space-y-3">
            <textarea
              className="input-field"
              placeholder="Goal (e.g. Summarize my project docs and schedule daily reminders)"
              value={goal}
              onChange={(e) => setGoal(e.target.value)}
            />
            <button className="btn-primary" onClick={runAgent} disabled={loading}>
              {loading ? 'Running...' : 'Run Agent'}
            </button>
          </div>

          <div className="mt-6 space-y-4">
            {runs.map((r) => (
              <div key={r.id} className="card">
                <div className="flex items-center justify-between gap-4">
                  <p className="font-semibold text-gray-900">{r.goal}</p>
                  <span className="text-xs px-2 py-1 rounded bg-gray-100">{r.status}</span>
                </div>

                {r.final_output && <p className="text-sm text-gray-700 mt-2 whitespace-pre-wrap">{r.final_output}</p>}
                {r.error_text && <p className="text-sm text-red-600 mt-2">{r.error_text}</p>}

                {r.status === 'completed' && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {planType === 'ENTERPRISE' ? (
                      <>
                        <button className="btn-secondary text-xs" onClick={() => generateMindmap(r.id)}>Generate Mindmap</button>
                        <button className="btn-secondary text-xs" onClick={() => generateDoc(r.id, 'strategy_memo')}>Strategy Memo</button>
                        <button className="btn-secondary text-xs" onClick={() => generateDoc(r.id, 'action_plan')}>Action Plan</button>
                        <button className="btn-secondary text-xs" onClick={() => generateDoc(r.id, 'project_brief')}>Project Brief</button>
                        <button className="btn-secondary text-xs" onClick={() => generatePlan(r.id)}>Generate Execution Plan</button>
                        <button className="btn-secondary text-xs" disabled={applyingRun === r.id} onClick={() => applyPlan(r.id)}>
                          {applyingRun === r.id ? 'Applying...' : 'Apply Plan to Schedules'}
                        </button>
                      </>
                    ) : (
                      <span className="text-xs text-gray-500">Advanced outputs (mindmaps/docs/plans) are on Enterprise.</span>
                    )}
                  </div>
                )}

                {planByRun[r.id]?.tasks?.length > 0 && (
                  <div className="mt-3 border border-emerald-200 bg-emerald-50 rounded-lg p-3">
                    <p className="text-sm font-medium text-emerald-900">Execution Plan ({planByRun[r.id].tasks.length} tasks)</p>
                    {planByRun[r.id]?.gaps?.length > 0 && (
                      <p className="text-xs text-amber-700 mt-1">Gaps: {planByRun[r.id].gaps.slice(0, 3).join(' • ')}</p>
                    )}
                    <ul className="mt-2 space-y-1">
                      {planByRun[r.id].tasks.slice(0, 6).map((t, i) => (
                        <li key={i} className="text-xs text-emerald-800">• {t.title} <span className="text-emerald-700">({t.cadence || 'none'})</span></li>
                      ))}
                    </ul>
                  </div>
                )}

                {r.steps?.length > 0 && (
                  <div className="mt-3 border-t pt-3 space-y-2">
                    {r.steps.map((s) => (
                      <div key={s.id} className="text-xs text-gray-600">
                        <span className="font-medium">#{s.step_index} {s.step_type}</span>
                        {s.tool_name ? ` • ${s.tool_name}` : ''}
                        {s.content ? ` — ${s.content}` : ''}
                        {s.tool_output ? ` — ${s.tool_output}` : ''}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default Agent;
